import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Folder as FolderIcon,
  SmartToy as AgentIcon,
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  QuestionAnswer as QuestionIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { fileSourcesApi, agentsApi, testRunsApi, questionBasesApi } from '../services/api';

const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336'];

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    fileSources: 0,
    questionBases: 0,
    agents: 0,
    testRuns: 0,
    completedTests: 0,
    runningTests: 0,
    failedTests: 0,
  });
  const [recentTests, setRecentTests] = useState([]);
  const [scoreDistribution, setScoreDistribution] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sourcesRes, basesRes, agentsRes, testsRes] = await Promise.all([
        fileSourcesApi.list(),
        questionBasesApi.list(),
        agentsApi.list(),
        testRunsApi.list(),
      ]);

      const tests = testsRes.data;
      const completed = tests.filter((t) => t.status === 'completed');
      const running = tests.filter((t) => t.status === 'running');
      const failed = tests.filter((t) => t.status === 'failed');

      setStats({
        fileSources: sourcesRes.data.length,
        questionBases: basesRes.data.length,
        agents: agentsRes.data.length,
        testRuns: tests.length,
        completedTests: completed.length,
        runningTests: running.length,
        failedTests: failed.length,
      });

      setRecentTests(tests.slice(0, 5));

      // Calculate score distribution
      const distribution = [
        { name: 'Excelente (90-100)', value: 0 },
        { name: 'Bom (70-89)', value: 0 },
        { name: 'Regular (50-69)', value: 0 },
        { name: 'Ruim (0-49)', value: 0 },
      ];

      completed.forEach((test) => {
        if (test.overall_score >= 90) distribution[0].value++;
        else if (test.overall_score >= 70) distribution[1].value++;
        else if (test.overall_score >= 50) distribution[2].value++;
        else distribution[3].value++;
      });

      setScoreDistribution(distribution);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/file-sources')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Fontes de Dados
                  </Typography>
                  <Typography variant="h4">{stats.fileSources}</Typography>
                </Box>
                <FolderIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/question-bases')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Bases de Perguntas
                  </Typography>
                  <Typography variant="h4">{stats.questionBases}</Typography>
                </Box>
                <QuestionIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/agents')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Agentes
                  </Typography>
                  <Typography variant="h4">{stats.agents}</Typography>
                </Box>
                <AgentIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/test-runs')}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Testes Realizados
                  </Typography>
                  <Typography variant="h4">{stats.testRuns}</Typography>
                </Box>
                <TestIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Distribuição de Scores
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={scoreDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {scoreDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Testes Recentes
            </Typography>
            {recentTests.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={recentTests}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="overall_score" fill="#1976d2" name="Score" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={300}
              >
                <Typography color="textSecondary">
                  Nenhum teste realizado ainda
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Tests List */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Últimos Testes
        </Typography>
        {recentTests.length > 0 ? (
          <Grid container spacing={2}>
            {recentTests.map((test) => (
              <Grid item xs={12} key={test.id}>
                <Card
                  variant="outlined"
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                  onClick={() => navigate(`/test-runs/${test.id}`)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={2}>
                        {test.status === 'completed' && (
                          <SuccessIcon color="success" />
                        )}
                        {test.status === 'running' && (
                          <PendingIcon color="warning" />
                        )}
                        {test.status === 'failed' && <ErrorIcon color="error" />}
                        {test.status === 'pending' && (
                          <PendingIcon color="disabled" />
                        )}
                        <Box>
                          <Typography variant="subtitle1">{test.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Agente: {test.agent_name} | Bases: {test.question_base_names?.join(', ') || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="h6" color="primary">
                          {test.overall_score ? `${test.overall_score.toFixed(1)}%` : '-'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {test.completed_questions}/{test.total_questions} perguntas
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="textSecondary">Nenhum teste realizado ainda</Typography>
        )}
      </Paper>
    </Box>
  );
}

export default Dashboard;
